import React, { useState, useEffect } from 'react';
import { X, Brain, TrendingUp, DollarSign, Calendar, PieChart, Filter, MessageSquare, Play } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BaseModal from '../common/BaseModal';
import { useToast } from '../../hooks/useToast';

const AiAnalysisModal = ({ isOpen, onClose, analysisResults, isLoading, onStartAnalysis, categoryOptions = [] }) => {
  const [showConfig, setShowConfig] = useState(true);
  const [analysisConfig, setAnalysisConfig] = useState({
    dateRange: {
      start: '',
      end: ''
    },
    categories: [],
    question: '',
    analysisType: 'comprehensive'
  });
  const { showToast, ToastContainer } = useToast();

  // Set default date range (last 3 months)
  useEffect(() => {
    if (isOpen && showConfig) {
      const today = new Date();
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 3);

      setAnalysisConfig(prev => ({
        ...prev,
        dateRange: {
          start: threeMonthsAgo.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        }
      }));
    }
  }, [isOpen, showConfig]);

  const handleStartAnalysis = () => {
    if (!analysisConfig.question.trim()) {
      showToast('Please enter a question for the AI analysis.', 'error');
      return;
    }

    setShowConfig(false);
    onStartAnalysis(analysisConfig);
  };

  const handleCategoryToggle = (categoryValue) => {
    setAnalysisConfig(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryValue)
        ? prev.categories.filter(c => c !== categoryValue)
        : [...prev.categories, categoryValue]
    }));
  };

  const predefinedQuestions = [
    "What are my spending patterns and where can I save money?",
    "Which categories am I overspending in compared to typical budgets?",
    "What trends do you see in my transaction history?",
    "Are there any unusual or suspicious transactions I should review?",
    "How does my spending vary by month and what causes the changes?",
    "What merchants do I spend the most with and why?",
    "Can you identify any subscription services or recurring charges?",
    "What are my biggest expenses and are they necessary?"
  ];
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    // If value is already in 0-100 range, use as-is. If in 0-1 range, multiply by 100
    const percentage = value > 1 ? value : value * 100;
    return `${percentage.toFixed(1)}%`;
  };

  return (
    <BaseModal isOpen={isOpen} onClose={() => { setShowConfig(true); onClose(); }} size="xl" showCloseButton={false}>
      {/* Custom Header with X button */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center">
          <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {showConfig ? 'Configure AI Analysis' : 'AI Transaction Analysis'}
          </h3>
        </div>
        <button
          onClick={() => {
            setShowConfig(true);
            onClose();
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">

        {/* Configuration Screen */}
        {showConfig && (
          <div className="space-y-6">
            {/* Date Range Section */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Date Range
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={analysisConfig.dateRange.start}
                    onChange={(e) => setAnalysisConfig(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, start: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={analysisConfig.dateRange.end}
                    onChange={(e) => setAnalysisConfig(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, end: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Category Filter Section */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Category Filter
                <span className="ml-2 text-sm font-normal text-gray-500">(optional - leave empty for all categories)</span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {categoryOptions.map((option) => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={analysisConfig.categories.includes(option.value)}
                      onChange={() => handleCategoryToggle(option.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm mr-1">{option.icon}</span>
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
              {analysisConfig.categories.length > 0 && (
                <div className="mt-3 text-sm text-blue-600">
                  {analysisConfig.categories.length} categories selected
                </div>
              )}
            </div>

            {/* Question Section */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Your Question
              </h4>

              {/* Predefined Questions */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Choose a predefined question:</label>
                <div className="space-y-2">
                  {predefinedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setAnalysisConfig(prev => ({ ...prev, question }))}
                      className="w-full text-left p-3 text-sm border border-gray-200 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Question Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Or write your own question:</label>
                <textarea
                  value={analysisConfig.question}
                  onChange={(e) => setAnalysisConfig(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="What would you like to know about your transactions?"
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Start Analysis Button */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfig(true);
                  onClose();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartAnalysis}
                disabled={!analysisConfig.question.trim()}
                className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="h-4 w-4 mr-2" />
                Start AI Analysis
              </button>
            </div>
          </div>
        )}

        {/* Loading Screen — shown only until the first streamed token arrives */}
        {!showConfig && isLoading && !analysisResults?.ai_analysis && (
          <div className="text-center py-12">
            <Brain className="h-12 w-12 animate-pulse mx-auto text-blue-500 mb-4" />
            <p className="text-gray-600">AI is analyzing your transactions...</p>
            <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
          </div>
        )}

        {/* Results Screen */}
        {!showConfig && analysisResults && (
          <div className="space-y-6">
            {/* AI Analysis Results */}
            {analysisResults.ai_analysis && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  AI Financial Analysis
                  {isLoading && (
                    <span className="ml-3 inline-flex items-center text-xs font-normal text-blue-500">
                      <Brain className="h-3.5 w-3.5 mr-1 animate-pulse" />
                      generating…
                    </span>
                  )}
                </h4>
                <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:text-gray-700 prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children, ...props }) => <h1 className="text-xl font-bold text-gray-900 mt-6 mb-3 first:mt-0" {...props}>{children}</h1>,
                      h2: ({ children, ...props }) => <h2 className="text-lg font-semibold text-gray-900 mt-5 mb-2" {...props}>{children}</h2>,
                      h3: ({ children, ...props }) => <h3 className="text-base font-medium text-gray-900 mt-4 mb-2" {...props}>{children}</h3>,
                      p: (props) => <p className="text-gray-700 mb-3 leading-relaxed" {...props} />,
                      ul: (props) => <ul className="list-disc pl-6 mb-3 space-y-1" {...props} />,
                      ol: (props) => <ol className="list-decimal pl-6 mb-3 space-y-1" {...props} />,
                      li: (props) => <li className="text-gray-700" {...props} />,
                      strong: (props) => <strong className="font-semibold text-gray-900" {...props} />,
                      em: (props) => <em className="italic text-gray-700" {...props} />,
                      code: ({ inline, ...props }) =>
                        inline
                          ? <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800" {...props} />
                          : <code className="block bg-gray-100 p-3 rounded-lg text-sm font-mono text-gray-800 overflow-x-auto" {...props} />,
                      blockquote: (props) => <blockquote className="border-l-4 border-blue-200 pl-4 italic text-gray-600 my-4" {...props} />
                    }}
                  >
                    {analysisResults.ai_analysis}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* Summary Section - Fallback for structured data (not while streaming) */}
            {!isLoading && !analysisResults.ai_analysis && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Analysis Summary
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {analysisResults.transaction_count || 0}
                    </p>
                    <p className="text-sm text-blue-700">Total Transactions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(analysisResults.total_amount)}
                    </p>
                    <p className="text-sm text-blue-700">Total Amount</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(analysisResults.total_amount / (analysisResults.transaction_count || 1))}
                    </p>
                    <p className="text-sm text-blue-700">Average Transaction</p>
                  </div>
                </div>
              </div>
            )}

            {/* Monthly Breakdown Section */}
            {analysisResults.monthly_breakdown && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Monthly Breakdown
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(analysisResults.monthly_breakdown).map(([month, data]) => (
                    <div key={month} className="bg-blue-50 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 mb-2">{month}</h5>
                      <div className="text-lg font-bold text-blue-600">
                        {formatCurrency(data.total_amount)}
                      </div>
                      <div className="text-sm text-blue-700 mb-1">
                        {data.transaction_count} transactions
                      </div>
                      {data.top_category && (
                        <div className="text-xs text-blue-600 mb-2">
                          Top: {data.top_category}
                        </div>
                      )}
                      {data.notable_transactions && data.notable_transactions.length > 0 && (
                        <div className="text-xs text-blue-600">
                          Notable: {data.notable_transactions.slice(0, 2).join(', ')}
                          {data.notable_transactions.length > 2 && '...'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Categories Section */}
            {analysisResults.categories && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <PieChart className="h-5 w-5 mr-2" />
                  Spending by Category
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(analysisResults.categories).map(([category, data]) => (
                    <div key={category} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium text-gray-900 capitalize">{category}</h5>
                        <span className="text-sm font-semibold text-green-600">
                          {formatPercentage(data.percentage)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>{formatCurrency(data.total_amount)}</span>
                        <span>{data.transaction_count} transactions</span>
                      </div>
                      <p className="text-xs text-gray-500">{data.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Merchants Section */}
            {analysisResults.top_merchants && analysisResults.top_merchants.length > 0 && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Top Merchants
                </h4>
                <div className="space-y-3">
                  {analysisResults.top_merchants.slice(0, 5).map((merchant, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <div>
                        <p className="font-medium text-gray-900">{merchant.merchant}</p>
                        <p className="text-sm text-gray-500 capitalize">{merchant.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(merchant.total_spent)}</p>
                        <p className="text-sm text-gray-500">{merchant.transaction_count} transactions</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Insights Section */}
            {analysisResults.insights && analysisResults.insights.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-yellow-800 mb-4">💡 Key Insights</h4>
                <ul className="space-y-2">
                  {analysisResults.insights.map((insight, index) => (
                    <li key={index} className="text-yellow-700 flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations Section */}
            {analysisResults.recommendations && analysisResults.recommendations.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-green-800 mb-4">🎯 Recommendations</h4>
                <ul className="space-y-2">
                  {analysisResults.recommendations.map((recommendation, index) => (
                    <li key={index} className="text-green-700 flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      {recommendation}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Spending Patterns */}
            {analysisResults.spending_patterns && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Spending Patterns
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysisResults.spending_patterns.highest_single_transaction && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h5 className="font-medium text-red-900 mb-2">Highest Single Transaction</h5>
                      <p className="text-lg font-bold text-red-600">
                        {formatCurrency(analysisResults.spending_patterns.highest_single_transaction.amount)}
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        {analysisResults.spending_patterns.highest_single_transaction.description}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        {analysisResults.spending_patterns.highest_single_transaction.date}
                      </p>
                    </div>
                  )}
                  {analysisResults.spending_patterns.most_frequent_category && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 mb-2">Most Frequent Category</h5>
                      <p className="text-lg font-bold text-blue-600 capitalize">
                        {analysisResults.spending_patterns.most_frequent_category}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analysis Metadata */}
            {analysisResults.metadata && (
              <div className="text-xs text-gray-500 text-center pt-4 border-t">
                Analysis completed on {new Date(analysisResults.metadata.analyzed_at).toLocaleString()}
                {analysisResults.metadata.transaction_count_analyzed < analysisResults.metadata.total_transactions && (
                  <span className="block mt-1">
                    * Analysis based on {analysisResults.metadata.transaction_count_analyzed} most recent transactions
                  </span>
                )}
              </div>
            )}
          </div>
        )}

      {/* Footer - only show if not in config mode */}
      {!showConfig && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            onClick={() => setShowConfig(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <Brain className="h-4 w-4 mr-2" />
            New Analysis
          </button>
          <button
            onClick={() => {
              setShowConfig(true);
              onClose();
            }}
            className="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      )}
      </div>

      <ToastContainer />
    </BaseModal>
  );
};

export default AiAnalysisModal;